export default class FileUtil {
  public static async convertHeicToJpeg(file: File): Promise<File> {
    if (!/\.(heic|heif)$/i.test(file.name)) {
      return file;
    }

    const jpegName = file.name.replace(/\.(heic|heif)$/i, '.jpg');

    // Prefer native browser HEIC decoding via <img> → canvas.
    // Chrome macOS 104+ and Safari can render HEIC in <img> tags using the
    // OS codec even when createImageBitmap does not support HEIC directly.
    const native = await FileUtil.heicToJpegViaCanvas(file, jpegName);
    if (native) {
      return native;
    }

    // Fallback: heic-to uses a newer libheif build and supports newer iPhone
    // HEIC variants. On any conversion error, return the original file so
    // callers using Promise.all don't fail the entire batch.
    try {
      const { heicTo } = await import('heic-to');
      const blob = await heicTo({ blob: file, type: 'image/jpeg', quality: 0.9 });

      return new File([blob], jpegName, { type: 'image/jpeg' });
    } catch (err) {
      console.warn(`convertHeicToJpeg: heic-to conversion failed for "${file.name}"`, err);

      return file;
    }
  }

  private static heicToJpegViaCanvas(file: File, jpegName: string): Promise<File | null> {
    return new Promise((resolve) => {
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(objectUrl);
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        canvas.getContext('2d').drawImage(img, 0, 0);
        canvas.toBlob(
          (blob) => resolve(blob ? new File([blob], jpegName, { type: 'image/jpeg' }) : null),
          'image/jpeg',
          0.9
        );
      };

      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        resolve(null);
      };

      img.src = objectUrl;
    });
  }

  public static getFileExtension(url: string) {
    return url.split('?')[0].split('.').pop().toLowerCase();
  }

  public static stripGetParams(url: string) {
    if (!url || !url.length) {
      return url;
    }

    return url.split('?')[0];
  }

  public static cleanAttachmentObjs(attachments: IXerceCaseAttachmentRequest[]) {
    const cleanedAttachments = attachments.map(({ data, ...attachment }) => ({
      ...attachment,
      fileName: FileUtil.stripGetParams(attachment.fileName),
      fileURL: FileUtil.stripGetParams(attachment.fileURL),
      title: FileUtil.stripGetParams(attachment.title),
    }));

    return cleanedAttachments;
  }
}
